import { createSlice, type SerializedError } from '@reduxjs/toolkit';

import type {
  CheckFlowBuilderTurnEndInput,
  FlowBuilderMessageDto,
  FlowBuilderSessionDto,
} from '../../types/flowBuilder/flowBuilderApi';
import type {
  FlowBuilderAgentState,
  FlowBuilderChat,
  FlowBuilderChatMessage,
  FlowBuilderState,
  FlowBuilderTurnFailure,
} from '../../types/flowBuilder/flowBuilderState';
import type { FlowBuilderTurnRejection } from '../../types/flowBuilder/flowBuilderStore';
import type { FlowValidationProblem } from '../../types/negotiationAgents';
import { isLiveTurnStatus } from '../../utils/flowBuilder/flowBuilderTurnStatus';
import {
  cancelFlowBuilderTurn,
  checkFlowBuilderTurnEnd,
  createFlowBuilderSession,
  fetchFlowBuilderMessages,
  fetchFlowBuilderSessions,
  flowBuilderDraftReloadRequested,
  flowBuilderNewSessionStarted,
  flowBuilderPreviewCleared,
  flowBuilderSessionOpened,
  flowBuilderStreamEventReceived,
  flowBuilderTurnReleasedLocally,
  resumeFlowBuilderTurn,
  startFlowBuilderTurn,
} from './actions';
import { createFlowBuilderAgentState, createFlowBuilderTurn } from './initialState';
import { applyFlowBuilderStreamEvent, finishFlowBuilderTurn } from './streamEventReducer';

const initialState: FlowBuilderState = { agents: {} };

function agentStateOf(state: FlowBuilderState, agentId: string): FlowBuilderAgentState {
  state.agents[agentId] ??= createFlowBuilderAgentState();
  return state.agents[agentId];
}

const chatFor = (sessionId: string | null): FlowBuilderChat => ({
  ...createFlowBuilderAgentState().chat,
  sessionId,
});

const toChatMessage = (message: FlowBuilderMessageDto): FlowBuilderChatMessage => ({
  key: `seq:${message.seq}`,
  role: message.role,
  content: message.content,
  turnId: message.turnId,
  createdAt: message.createdAt,
});

const turnRoleKey = (message: FlowBuilderChatMessage) => `${message.turnId}:${message.role}`;

/**
 * A página mais recente do servidor substitui o histórico, mas o turno que terminou aqui pode ainda
 * não estar nela; e a resposta vista ao vivo carrega a atividade do turno, que o servidor não tem.
 */
function mergeLatestPage(
  serverMessages: FlowBuilderChatMessage[],
  localMessages: FlowBuilderChatMessage[],
): FlowBuilderChatMessage[] {
  const localByTurnRole = new Map(
    localMessages
      .filter((message) => message.turnId)
      .map((message) => [turnRoleKey(message), message]),
  );
  const serverKeys = new Set(serverMessages.map(turnRoleKey));
  const withActivity = serverMessages.map((message) => {
    const activity = localByTurnRole.get(turnRoleKey(message))?.activity;
    return activity ? { ...message, activity } : message;
  });
  const localOnly = localMessages.filter(
    (message) => message.key.startsWith('turn:') && !serverKeys.has(turnRoleKey(message)),
  );
  return [...withActivity, ...localOnly];
}

function prependOlderPage(
  olderMessages: FlowBuilderChatMessage[],
  currentMessages: FlowBuilderChatMessage[],
): FlowBuilderChatMessage[] {
  const known = new Set(currentMessages.map((message) => message.key));
  return [...olderMessages.filter((message) => !known.has(message.key)), ...currentMessages];
}

function upsertSessionOnTop(agent: FlowBuilderAgentState, session: FlowBuilderSessionDto) {
  const others = agent.sessions.items.filter((item) => item.id !== session.id);
  if (others.length === agent.sessions.items.length) agent.sessions.total += 1;
  agent.sessions.items = [session, ...others];
}

const LOCAL_ABORT_FAILURE: FlowBuilderTurnFailure = {
  source: 'request',
  code: 'CANCELLED_LOCALLY',
  status: null,
  message: 'A geração foi interrompida neste navegador e pode continuar no servidor.',
};

function requestFailureOf(
  problem: FlowValidationProblem | undefined,
  error: SerializedError,
): FlowBuilderTurnFailure {
  return {
    source: 'request',
    code: problem?.code ?? 'REQUEST_FAILED',
    status: problem?.status ?? null,
    message: problem?.message ?? error.message ?? 'Não foi possível falar com o FlowBuilder.',
  };
}

/** Sem recusa definitiva do core (4xx), o turno pode ter nascido e seguir no worker. */
const serverMayStillRunTurn = (failure: FlowBuilderTurnFailure): boolean =>
  failure.source === 'request' && (failure.status === null || failure.status >= 500);

function failLiveTurn(
  state: FlowBuilderState,
  agentId: string,
  turnId: string,
  failure: FlowBuilderTurnFailure,
) {
  const agent = state.agents[agentId];
  const turn = agent?.turn;
  if (!turn || turn.turnId !== turnId || turn.phase === 'finished') return;
  turn.failure = failure;
  finishFlowBuilderTurn(agent, turn, { content: turn.streamingText.trim(), interrupted: true });
  if (serverMayStillRunTurn(failure)) agent.unconfirmedTurn = { turnId, checking: false };
}

function unconfirmedTurnOf(
  state: FlowBuilderState,
  { agentId, turnId }: CheckFlowBuilderTurnEndInput,
) {
  const agent = state.agents[agentId];
  return agent?.unconfirmedTurn?.turnId === turnId ? agent : null;
}

/** Turno que caiu sem `done`/`error` no stream fecha com a falha do transporte. */
function failTurnRequest(
  state: FlowBuilderState,
  { agentId, turnId, aborted, problem, error }: FlowBuilderTurnRejection,
) {
  const failure = aborted ? LOCAL_ABORT_FAILURE : requestFailureOf(problem, error);
  failLiveTurn(state, agentId, turnId, failure);
}

/** Trocar de conversa descarta o aviso do turno que já acabou; turno vivo nunca é descartado. */
function resetChat(agent: FlowBuilderAgentState, sessionId: string | null) {
  agent.chat = chatFor(sessionId);
  agent.pendingInterview = null;
  if (agent.turn?.phase === 'finished') agent.turn = null;
}

const slice = createSlice({
  name: 'flowBuilder',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(flowBuilderStreamEventReceived, (state, action) => {
      const agent = state.agents[action.payload.agentId];
      if (agent) applyFlowBuilderStreamEvent(agent, action.payload);
    });
    builder.addCase(flowBuilderSessionOpened, (state, action) => {
      resetChat(agentStateOf(state, action.payload.agentId), action.payload.sessionId);
    });
    builder.addCase(flowBuilderNewSessionStarted, (state, action) => {
      resetChat(agentStateOf(state, action.payload.agentId), null);
    });
    builder.addCase(flowBuilderPreviewCleared, (state, action) => {
      const agent = state.agents[action.payload.agentId];
      if (agent) delete agent.previews[action.payload.flowId];
    });
    builder.addCase(flowBuilderDraftReloadRequested, (state, action) => {
      const agent = agentStateOf(state, action.payload.agentId);
      agent.serverReloads.agent += 1;
      if (agent.turn) agent.turn.draftConflict = null;
    });

    builder.addCase(fetchFlowBuilderSessions.pending, (state, action) => {
      const sessions = agentStateOf(state, action.meta.arg.agentId).sessions;
      sessions.status = 'loading';
      sessions.error = null;
      sessions.requestId = action.meta.requestId;
    });
    builder.addCase(fetchFlowBuilderSessions.fulfilled, (state, action) => {
      const sessions = agentStateOf(state, action.meta.arg.agentId).sessions;
      if (sessions.requestId !== action.meta.requestId) return;
      const loaded = new Set(action.payload.items.map((session) => session.id));
      sessions.items =
        action.payload.page === 1
          ? action.payload.items
          : [
              ...sessions.items.filter((session) => !loaded.has(session.id)),
              ...action.payload.items,
            ];
      sessions.page = action.payload.page;
      sessions.total = action.payload.total;
      sessions.hasMore = action.payload.hasMore;
      sessions.status = 'success';
    });
    builder.addCase(fetchFlowBuilderSessions.rejected, (state, action) => {
      const sessions = agentStateOf(state, action.meta.arg.agentId).sessions;
      if (sessions.requestId !== action.meta.requestId) return;
      sessions.status = 'error';
      sessions.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Não foi possível carregar as conversas.',
      };
    });

    builder.addCase(createFlowBuilderSession.pending, (state, action) => {
      agentStateOf(state, action.meta.arg.agentId).sessionCreation = {
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(createFlowBuilderSession.fulfilled, (state, action) => {
      const agent = agentStateOf(state, action.meta.arg.agentId);
      agent.sessionCreation = { status: 'success', error: null };
      upsertSessionOnTop(agent, action.payload);
      if (agent.chat.sessionId === null) agent.chat.sessionId = action.payload.id;
      if (agent.turn?.sessionId === null) agent.turn.sessionId = action.payload.id;
    });
    builder.addCase(createFlowBuilderSession.rejected, (state, action) => {
      agentStateOf(state, action.meta.arg.agentId).sessionCreation = {
        status: 'error',
        error: action.payload ?? {
          code: 'REQUEST_FAILED',
          message: 'Não foi possível criar a conversa.',
        },
      };
    });

    builder.addCase(fetchFlowBuilderMessages.pending, (state, action) => {
      const chat = agentStateOf(state, action.meta.arg.agentId).chat;
      if (chat.sessionId !== action.meta.arg.sessionId) return;
      chat.status = 'loading';
      chat.error = null;
      chat.requestId = action.meta.requestId;
    });
    builder.addCase(fetchFlowBuilderMessages.fulfilled, (state, action) => {
      const chat = agentStateOf(state, action.meta.arg.agentId).chat;
      if (chat.sessionId !== action.meta.arg.sessionId) return;
      if (chat.requestId !== action.meta.requestId) return;
      const page = action.payload.items.map(toChatMessage);
      chat.messages =
        action.meta.arg.beforeSeq === undefined
          ? mergeLatestPage(page, chat.messages)
          : prependOlderPage(page, chat.messages);
      chat.nextBeforeSeq = action.payload.nextBeforeSeq;
      chat.hasMore = action.payload.hasMore;
      chat.status = 'success';
    });
    builder.addCase(fetchFlowBuilderMessages.rejected, (state, action) => {
      const chat = agentStateOf(state, action.meta.arg.agentId).chat;
      if (chat.sessionId !== action.meta.arg.sessionId) return;
      if (chat.requestId !== action.meta.requestId) return;
      chat.status = 'error';
      chat.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Não foi possível carregar o histórico.',
      };
    });

    builder.addCase(startFlowBuilderTurn.pending, (state, action) => {
      const { agentId, turnId, sessionId, message, expectedDraftRevision } = action.meta.arg;
      const agent = agentStateOf(state, agentId);
      agent.turn = createFlowBuilderTurn({
        turnId,
        sessionId,
        userMessage: message,
        startRevision: expectedDraftRevision,
      });
      agent.pendingInterview = null;
      agent.previews = {};
      agent.chat.messages.push({
        key: `turn:${turnId}:user`,
        role: 'USER',
        content: message,
        turnId,
        createdAt: null,
      });
    });
    builder.addCase(startFlowBuilderTurn.rejected, (state, action) => {
      failTurnRequest(state, {
        agentId: action.meta.arg.agentId,
        turnId: action.meta.arg.turnId,
        aborted: action.meta.aborted,
        problem: action.payload,
        error: action.error,
      });
    });

    builder.addCase(resumeFlowBuilderTurn.pending, (state, action) => {
      const { agentId, turnId, sessionId } = action.meta.arg;
      const agent = agentStateOf(state, agentId);
      agent.turn = createFlowBuilderTurn({
        turnId,
        sessionId,
        userMessage: null,
        startRevision: null,
      });
      agent.pendingInterview = null;
      agent.previews = {};
    });
    builder.addCase(resumeFlowBuilderTurn.rejected, (state, action) => {
      failTurnRequest(state, {
        agentId: action.meta.arg.agentId,
        turnId: action.meta.arg.turnId,
        aborted: action.meta.aborted,
        problem: action.payload,
        error: action.error,
      });
    });

    builder.addCase(cancelFlowBuilderTurn.pending, (state, action) => {
      const turn = state.agents[action.meta.arg.agentId]?.turn;
      if (turn?.turnId === action.meta.arg.turnId && turn.phase === 'streaming') {
        turn.phase = 'cancelling';
      }
    });
    builder.addCase(cancelFlowBuilderTurn.rejected, (state, action) => {
      const turn = state.agents[action.meta.arg.agentId]?.turn;
      if (turn?.turnId === action.meta.arg.turnId && turn.phase === 'cancelling') {
        turn.phase = 'streaming';
      }
    });
    builder.addCase(flowBuilderTurnReleasedLocally, (state, action) => {
      const { agentId, turnId } = action.payload;
      const wasLive = state.agents[agentId]?.turn?.turnId === turnId;
      failLiveTurn(state, agentId, turnId, LOCAL_ABORT_FAILURE);
      // Liberar aqui não para o worker: o canvas relê o que ele gravou até agora, e o save fica
      // travado até `checkFlowBuilderTurnEnd` confirmar o fim e pedir a recarga final.
      if (wasLive) agentStateOf(state, agentId).serverReloads.agent += 1;
    });
    builder.addCase(checkFlowBuilderTurnEnd.pending, (state, action) => {
      const agent = unconfirmedTurnOf(state, action.meta.arg);
      if (agent?.unconfirmedTurn) agent.unconfirmedTurn.checking = true;
    });
    builder.addCase(checkFlowBuilderTurnEnd.fulfilled, (state, action) => {
      const agent = unconfirmedTurnOf(state, action.meta.arg);
      if (!agent?.unconfirmedTurn) return;
      if (isLiveTurnStatus(action.payload.status)) {
        agent.unconfirmedTurn.checking = false;
        return;
      }
      agent.unconfirmedTurn = null;
      agent.serverReloads.agent += 1;
    });
    builder.addCase(checkFlowBuilderTurnEnd.rejected, (state, action) => {
      const agent = unconfirmedTurnOf(state, action.meta.arg);
      if (!agent?.unconfirmedTurn) return;
      // 404: o core não tem o turno para este admin (o POST não chegou a criá-lo); nada a esperar.
      if (action.payload?.status === 404) agent.unconfirmedTurn = null;
      else agent.unconfirmedTurn.checking = false;
    });
  },
});

export default slice.reducer;
